import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Calendar as CalendarIcon } from "lucide-react";

const inputCls =
  "border-0 border-b border-slate-300 rounded-none bg-transparent shadow-none " +
  "font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0 " +
  "focus-visible:border-[#1F2937] px-0";

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Convert a browser <input type=datetime-local> value ("2026-03-04T14:30")
// into an ISO 8601 UTC string ("2026-03-04T19:30:00Z").
function localToUtcISO(local) {
  if (!local) return "";
  const d = new Date(local);
  return isNaN(d.getTime()) ? "" : d.toISOString();
}

export default function MeetingsPanel({ app, canEdit }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "",
    starts_at: "",
    duration_minutes: 30,
    description: "",
    invite_borrower: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/applications/${app.id}/meetings`)
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (app?.id) load();
     
  }, [app?.id]);

  const submit = async () => {
    if (!form.title.trim() || !form.starts_at) {
      toast.error("Title and start time required");
      return;
    }
    const starts_at = localToUtcISO(form.starts_at);
    if (!starts_at) {
      toast.error("Invalid start time");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/applications/${app.id}/meetings`, {
        title: form.title.trim(),
        starts_at,
        duration_minutes: Number(form.duration_minutes) || 30,
        description: form.description || null,
        invite_borrower: !!form.invite_borrower,
      });
      toast.success("Meeting scheduled — reminders will fire 24h and 1h before");
      setForm({ title: "", starts_at: "", duration_minutes: 30, description: "", invite_borrower: false });
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    if (!confirm("Cancel this meeting? Reminders will stop.")) return;
    try {
      await api.delete(`/meetings/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upcoming = items.filter((m) => new Date(m.starts_at) > new Date());
  const past = items.filter((m) => new Date(m.starts_at) <= new Date());

  return (
    <div className="hair-y py-8" data-testid="meetings-panel">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
        <div className="small-caps text-slate-500">
          Meetings
          {items.length > 0 && (
            <span className="ml-3 text-slate-400">
              {upcoming.length} upcoming
            </span>
          )}
        </div>
        {canEdit && !creating && (
          <button
            onClick={() => setCreating(true)}
            className="group inline-flex items-center gap-3"
            data-testid="meeting-schedule-btn"
          >
            <span className="h-[1px] w-8 bg-slate-800 transition-all group-hover:w-12" />
            <span className="small-caps text-[#1F2937]">Schedule a meeting</span>
          </button>
        )}
      </div>

      {creating && (
        <div className="border border-slate-200 p-6 mb-8 space-y-6" data-testid="meeting-form">
          <div>
            <div className="small-caps text-slate-500 mb-2">Title</div>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Kickoff call, underwriting review, closing check-in…"
              className={inputCls}
              data-testid="meeting-title"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <div className="small-caps text-slate-500 mb-2">When</div>
              <Input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                className={inputCls}
                data-testid="meeting-datetime"
              />
            </div>
            <div>
              <div className="small-caps text-slate-500 mb-2">Duration (min)</div>
              <Input
                type="number"
                min={5}
                max={240}
                step={5}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                className={inputCls}
                data-testid="meeting-duration"
              />
            </div>
          </div>

          <div>
            <div className="small-caps text-slate-500 mb-2">Notes (optional)</div>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Agenda, dial-in link, anything to remember…"
              className={inputCls + " resize-none"}
              data-testid="meeting-description"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.invite_borrower}
              onChange={(e) => setForm({ ...form, invite_borrower: e.target.checked })}
              className="h-4 w-4 border border-slate-400"
              data-testid="meeting-invite-borrower"
            />
            <span className="font-semibold text-[15px] text-[#1F2937]">
              Also invite the borrower
            </span>
          </label>

          <div className="flex items-center gap-6 pt-2">
            <button
              onClick={submit}
              disabled={submitting}
              className="group inline-flex items-center gap-3 disabled:opacity-50"
              data-testid="meeting-save-btn"
            >
              <span className="h-[1px] w-12 bg-slate-800 transition-all group-hover:w-20" />
              <span className="small-caps text-[#1F2937]">
                {submitting ? "Scheduling…" : "Schedule"}
              </span>
            </button>
            <button
              onClick={() => setCreating(false)}
              className="small-caps text-slate-500 hover:text-[#1F2937]"
              data-testid="meeting-cancel-btn"
            >
              Cancel
            </button>
          </div>
          <div className="small-caps text-slate-400 pt-2">
            Reminders fire 24 hours and 1 hour before start. Email to everyone;
            SMS 1 hour before for anyone with a phone on file.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center font-semibold text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center font-semibold text-slate-400 text-[15px]">
          No meetings scheduled yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 border-y border-slate-200">
          {[...upcoming, ...past].map((m) => {
            const isPast = new Date(m.starts_at) <= new Date();
            return (
              <li key={m.id} className="py-4 flex items-start gap-4" data-testid={`meeting-${m.id}`}>
                <CalendarIcon className={`h-4 w-4 mt-1 ${isPast ? "text-slate-300" : "text-[#1F2937]"}`} />
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-[15px] ${isPast ? "text-slate-400" : "text-[#1F2937]"}`}>
                    {m.title}
                    {isPast && <span className="ml-2 small-caps text-slate-400 text-[10px]">Past</span>}
                  </div>
                  <div className="text-[13px] text-slate-500 mt-1">
                    {fmt(m.starts_at)} · {m.duration_minutes} min
                  </div>
                  <div className="text-[12px] text-slate-500 mt-1">
                    {m.attendees.map((a) => a.name || a.email).join(", ")}
                  </div>
                  {m.description && (
                    <div className="text-[13px] text-slate-600 mt-2 whitespace-pre-wrap">{m.description}</div>
                  )}
                </div>
                {canEdit && !isPast && (
                  <button
                    onClick={() => cancel(m.id)}
                    className="text-slate-400 hover:text-[#1F2937]"
                    title="Cancel meeting"
                    data-testid={`meeting-cancel-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
